import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import type {
  MarketplaceOrderHandoffDefaults,
  TargetRatio,
  TargetRatioPlatformFamily,
} from "@nexus/contracts"
import { Button, Icon } from "@nexus/ui"
import {
  MarketplaceOrderHandoffDefaultsForm,
  OrderHandoffOverrideNoteField,
} from "./MarketplaceOrderHandoffDefaultsForm"
import { TargetRatiosPicker } from "./TargetRatiosPicker"

export interface SendOrderHandoffStepProps {
  handoffOverrideNote: string
  onHandoffOverrideNoteChange: (next: string) => void
  handoffDefaults: MarketplaceOrderHandoffDefaults
  onHandoffDefaultsChange: (next: MarketplaceOrderHandoffDefaults) => void
  handoffDefaultsBaseline?: MarketplaceOrderHandoffDefaults
  platformFamily: TargetRatioPlatformFamily
  targetRatios: readonly TargetRatio[]
  onToggleTargetRatio: (ratio: TargetRatio) => void
  ratiosReady?: boolean
  showEmptyError?: boolean
  disabled?: boolean
  isLoadingDefaults?: boolean
  defaultsFailed?: boolean
  onRetryDefaults?: () => void
  /** Rendered under the order instructions; the dialog owns its state. */
  scriptReferencesField?: ReactNode
}

export function SendOrderHandoffStep({
  handoffOverrideNote,
  onHandoffOverrideNoteChange,
  handoffDefaults,
  onHandoffDefaultsChange,
  handoffDefaultsBaseline,
  platformFamily,
  targetRatios,
  onToggleTargetRatio,
  ratiosReady = true,
  showEmptyError,
  disabled = false,
  isLoadingDefaults = false,
  defaultsFailed = false,
  onRetryDefaults,
  scriptReferencesField,
}: SendOrderHandoffStepProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-5">
      <TargetRatiosPicker
        family={platformFamily}
        selected={targetRatios}
        onToggle={onToggleTargetRatio}
        isReady={ratiosReady}
        showEmptyError={showEmptyError}
        disabled={disabled}
      />
      <p className="text-xs leading-relaxed text-muted-foreground">
        {t(
          "marketplace.sendHandoff.thumbnailRequest",
          "The partner email asks for one thumbnail per selected ratio, or one default thumbnail when none are selected. Optional, but expected.",
        )}
      </p>
      {defaultsFailed ? (
        <div className="flex flex-col items-start gap-2 py-3">
          <p className="text-sm font-medium text-foreground">
            {t("marketplace.sendHandoff.defaultsFailed", "Couldn't load order brief defaults.")}
          </p>
          {onRetryDefaults ? (
            <Button size="sm" variant="outline" onClick={onRetryDefaults}>
              {t("common.retry", "Retry")}
            </Button>
          ) : null}
        </div>
      ) : isLoadingDefaults ? (
        <div role="status" className="flex items-center gap-2 py-3">
          <Icon name="loader" size="xs" className="animate-spin text-muted-foreground" />
          <p className="text-xs text-muted-foreground">
            {t("marketplace.sendHandoff.loadingDefaults", "Loading order brief defaults…")}
          </p>
        </div>
      ) : (
        <>
          <OrderHandoffOverrideNoteField
            value={handoffOverrideNote}
            onChange={onHandoffOverrideNoteChange}
            disabled={disabled}
          />
          {scriptReferencesField}
          <MarketplaceOrderHandoffDefaultsForm
            variant="order-send"
            value={handoffDefaults}
            onChange={onHandoffDefaultsChange}
            baseline={handoffDefaultsBaseline}
            disabled={disabled}
          />
        </>
      )}
    </div>
  )
}
